import "../../../components/common/Tables/DataTable.css"
import React, {useEffect, useMemo, useState} from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import RootState from "../../../redux/states/root.state";
import { fetchDealershipUsers } from "../../../redux/actions/dealership.actions";
import { AppDispatch } from "../../../redux/store";
import { DataTableFoot, SortButton } from "../../../components/common/Tables/DataTableParts";

type Order = 'asc' | 'desc';
const headers = [
  {id:'id', label:'ID', align:null},
  {id:'name', label:'User', align:null},
  {id:'username', label:'Username', align:null},
  {id:'roles', label:'Role', align:null},
];

// Sorted on this page only (the API doesn't sort staff)
const sortValue = (user: any, column: string) => {
  const value = column === 'roles' ? user?.roles?.[0]?.name : user?.[column];
  return (value ?? '').toString().toLowerCase();
};

interface DealershipUsersTableProps {
  // Page controls (Add / Remove Staff) shown in the table's toolbar
  toolbar?: React.ReactNode;
}

const DealershipUsersTable: React.FC<DealershipUsersTableProps> = ({toolbar}) => {
  const navigate = useNavigate();
  const {id} = useParams();
  const dispatch: AppDispatch = useDispatch();
  const {data:users, loading:isLoading, meta} = useSelector((state: RootState) => state.dealershipUsers);
  const [searchParams, setSearchParams] = useSearchParams();

  // Table Sort
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState('');

  useEffect(() => {
    if (id){
      dispatch(fetchDealershipUsers({id:id, params: searchParams.toString()}));
    }
  }, [dispatch,id,searchParams]);

  const rows = useMemo(() => {
    if (!orderBy) return users;
    const direction = order === 'asc' ? 1 : -1;
    return [...users].sort((a:any, b:any) => sortValue(a, orderBy).localeCompare(sortValue(b, orderBy), undefined, {numeric: true}) * direction);
  }, [users, orderBy, order]);

  const handleRequestSort = (header_id: string) => {
    const isAsc = orderBy === header_id && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(header_id);
  };

  const page = searchParams.get("page") ? Number(searchParams.get("page")) : 1;
  const totalPages = meta?.last_page ?? 1;
  const total = meta?.total ?? 0;

  const goToPage = (value: number) => {
    searchParams.set("page", String(value));
    setSearchParams(searchParams);
  }

  return (
    <div className="dt">
      {toolbar && <div className="dt-toolbar">
        <div className="dt-toolbar-start">{toolbar}</div>
      </div>}

      <div className="dt-scroll">
        {isLoading ? (
          <div className="dt-state"><span className="spinner" role="progressbar" aria-label="Loading staff"/></div>
        ) : users.length > 0 ? (
          <table className="dt-table">
            <thead>
              <tr>
                {headers.map((header)=>(
                  <th
                    key={header.id}
                    scope="col"
                    aria-sort={orderBy === header.id ? (order === 'asc' ? 'ascending' : 'descending') : undefined}
                  >
                    <SortButton label={header.label} active={orderBy === header.id} order={order} onClick={()=>handleRequestSort(header.id)}/>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {rows.map((user:any, i:number)=>(
                <tr key={user?.id ?? i}>
                  <td>
                    <a
                      href={`/user/${user.id}`}
                      className="dt-guid"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`View user ${user?.name ?? user?.id}`}
                      onClick={(e) => {
                        if (e.metaKey || e.ctrlKey || e.shiftKey) return;
                        e.preventDefault();
                        navigate(`/user/${user.id}`);
                      }}
                    >
                      {user?.id}
                    </a>
                  </td>
                  <td className="dt-strong">{user?.name}</td>
                  <td>{user?.username}</td>
                  <td>{user?.roles?.[0]?.name ? <span className="dt-role">{user.roles[0].name}</span> : <span className="dt-na">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="dt-state">
            <div className="dt-empty-title">No Users Found</div>
          </div>
        )}
      </div>

      <DataTableFoot
        shown={users?.length ?? 0}
        total={total}
        noun={total === 1 ? 'User' : 'Users'}
        page={page}
        totalPages={totalPages}
        loading={isLoading}
        onPage={goToPage}
      />
    </div>
  );
};

export default DealershipUsersTable;
